import styles from "./master_schedule_slots.module.css";
import { Button, Checkbox, Col, Row, Segmented, Select, Space, Spin, Tooltip } from "antd";
import Title from "antd/es/typography/Title";
import dayjs, { Dayjs } from "dayjs";
import { InputDateTimeFormat, Permission, ProductionLine } from "../../api/models";
import { useContext, useState } from "react";
import { ConfiguratorContext } from "../../context";
import Utils from "../../util/util";
import { CalendarOutlined, CarryOutOutlined, TruckOutlined } from "@ant-design/icons";
import AllProductionLineEvents from "./AllProductionLineEvents";
import MSMonthCalendar from "./MSMonthCalendar";
import {ArrayParam, NumberParam, StringParam, useQueryParam} from "use-query-params";
import MasterScheduleEventButtonModal from "./MasterScheduleEventButtonModal";
import useMasterScheduleEvents from "../../swr/useMasterScheduleEvents";
import MasterScheduleSlotsContextProvider from "../../contexts/MasterScheduleSlotsContext";
import MSSlotCalendar from "./MSSlotCalendar";

export const ALL_LINES = [ProductionLine.LINE_1, ProductionLine.LINE_2, ProductionLine.LINE_3];

enum Views {
  Month,
  Events,
  Slot,
}

const MasterScheduleSlots = () => {

  const [viewParam, setViewParam] = useQueryParam<number | null | undefined>("vw", NumberParam);
  const [selectedDateParam, setSelectedDateParam] = useQueryParam<string | null | undefined>("dt", StringParam );
  const [selectedLinesParam, setSelectedLinesParam] = useQueryParam<(string | null)[]|undefined|null>("l", ArrayParam);

  const configurator = useContext(ConfiguratorContext);
  const [selectedProductionLines, setSelectedProductionLines] = useState<ProductionLine[]>(
      ( selectedLinesParam?.map(v => v && Utils.stringToEnum(v, ProductionLine))) as ProductionLine[] );
  const [selectedDate, setSelectedDate] = useState<Dayjs>( dayjs( selectedDateParam ) );
  const [calendarView, setCalendarView] = useState<Views>(viewParam || Views.Month);
  const [refresh, setRefresh] = useState(false);
  const [showDefaultEvent, setShowDefaultEvent] = useState(false);
  const [showAllEvents, setShowAllEvents] = useState(false);
  const [selectedOption, setSelectedOption] = useState<string[]>();

  const selectedMonth = selectedDate?.startOf('month');

  const hasEditPermissions = configurator.isAdmin() || configurator.hasPermission(Permission.MASTER_SCHEDULE_WRITE);

  const lineWithEventsAsync = useMasterScheduleEvents({
    startDate: selectedMonth.subtract(1, 'week').format(InputDateTimeFormat),
    endDate: selectedMonth.add(3, 'month').add(1, 'week').format(InputDateTimeFormat),
    productionLines: !selectedProductionLines?.length
        ? Object.values(ProductionLine)
        : selectedProductionLines
  });

  const handleProductionLineChange = (value: string[]) => {
      setSelectedProductionLines(( value?.map(v => v && Utils.stringToEnum(v, ProductionLine))) as ProductionLine[] );
      setSelectedOption(value);
      setSelectedLinesParam(value)
  };

  const handleSelectDay = (value: Dayjs) => {
    setCalendarView(Views.Slot)
    setViewParam(Views.Slot);
  }

  const handleDateChange = (value: Dayjs) => {
    setSelectedDate(value);
    setSelectedDateParam( (value.month() !== dayjs()?.month()) ? value.format("YYYY-MM-DD") : undefined );
  }

  const handleAddEvent = () => {
    lineWithEventsAsync.mutate();
  }

  const handleChangeEvent = () => {
    lineWithEventsAsync.mutate();
  }

  const handleSelectSlot = () => {
    console.log("handleSelectSlot");
  }

  const onSwitchSegment = (v:Views) => {
    setCalendarView(v);
    setViewParam(v);
  }

  return <div className={styles["master-schedule-slots"]}>
    <MasterScheduleSlotsContextProvider value={{
      selectedDate,
      lineWithEventsAsync: lineWithEventsAsync,
      selectedProductionLines
    }} >

    <div className="site-layout-background">
      <Space direction="vertical" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: "space-between", gap: '5px' }}>
          <Title level={2}>Master Schedule Slot</Title>
        </div>

        <Row justify={"space-between"}>

          <Col>
          <Row>
            {hasEditPermissions &&
              <Segmented
                options={[ 
                  {
                    value: Views.Month,
                    icon:  <Tooltip title="Calendar">
                      <CalendarOutlined />
                    </Tooltip>
                  },
                  {
                    value: Views.Slot,
                    icon: <Tooltip title="Slots">
                      <TruckOutlined />
                    </Tooltip>
                  },
                  {
                    value: Views.Events,
                    icon: <Tooltip title="Events">
                      <CarryOutOutlined />
                    </Tooltip>
                  }
                ]}
                onChange={onSwitchSegment}
                value={calendarView}
                style={{marginRight: "20px"}}
              />}

            <Select
              placeholder="Select production line"
              className={styles["line-selector"]}
              mode="multiple"
              onChange={handleProductionLineChange}
              value={selectedOption}
              allowClear
              style={{minWidth: "20rem"}}
              options={Object.values(ProductionLine).map((line) => ({
                    key: line,
                    value: line,
                    label: Utils.snakeCaseToFirstLetterCapitalized(line)}
              ))}
            />
            {calendarView === Views.Events &&
                <Checkbox style={{marginLeft: "20px"}} onChange={(event) => setShowDefaultEvent(event.target.checked)}>Default Event</Checkbox>}
            </Row>
          </Col>

          <Col>
              {(calendarView === Views.Month)
                ? hasEditPermissions &&
                  <MasterScheduleEventButtonModal
                      type="primary" style={{marginRight: "10px"}}
                      startDate={selectedDate}
                      onChange={handleAddEvent}
                  >
                    Add Event
                    </MasterScheduleEventButtonModal>
                  : calendarView === Views.Events &&
                  <Space>
                    <Button type="primary" onClick={() => {setShowAllEvents((prev) => !prev)}}>{showAllEvents ? "Month Event" : "All Events" }</Button>
                    <Button type="primary" onClick={() => setRefresh(!refresh)} style={{marginRight: "10px"}}>Refresh</Button>
                  </Space>}
          </Col>
        </Row>
        <div>
            <Spin spinning={lineWithEventsAsync.isLoading}>
              {(calendarView === Views.Month)
                    ? <MSMonthCalendar
                        onSelectDay={handleSelectDay}
                        onDateChange={handleDateChange}
                        isReadOnly={!hasEditPermissions}
                        onChangeEvent={handleChangeEvent}
                    />
                : (calendarView === Views.Slot)
                    ? <MSSlotCalendar
                        onDateChange={handleDateChange}
                        onChangeEvent={handleChangeEvent}
                        onSelectSlot={handleSelectSlot}
                        isReadOnly={!hasEditPermissions}
                    />
                : <AllProductionLineEvents
                    refresh={refresh}
                    setRefresh={setRefresh}
                    showDefaultEvent={showDefaultEvent}
                    showAllEvents={showAllEvents}
                    onDateChange={handleDateChange}
                /> }
            </Spin>
        </div>
      </Space>
    </div>
    </MasterScheduleSlotsContextProvider>
  </div>
}

export default MasterScheduleSlots;



